import React, { useEffect, useMemo, useState } from "react";
import api from "../../config/URL";
import { Link, useNavigate } from "react-router-dom";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  createTheme,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import GlobalDelete from "../../components/common/GlobalDelete";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "SENDNOTIFICATION";

/* ================= CSV EXPORT HELPER ================= */
const exportVisibleColumnsToCSV = (
  table,
  rows,
  fileName = "School_Announcement.csv",
) => {
  const visibleColumns = table
    .getAllLeafColumns()
    .filter((col) => col.getIsVisible() && col.columnDef.accessorKey);

  const headers = visibleColumns.map((col) => col.columnDef.header);

  const csvRows = rows.map((row) =>
    visibleColumns.map((col) => {
      const value = row[col.columnDef.accessorKey];
      return `"${value ?? ""}"`;
    }),
  );

  const csvContent = [
    headers.join(","),
    ...csvRows.map((r) => r.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};
/* ==================================================== */

const SendNotification = ({ selectedCenter }) => {
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");
  const [loading, setLoading] = useState(true);
  const [datas, setDatas] = useState([]);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const defaultColumnVisibility = {
    // messageTitle: false,
    // messageDescription: false,
    // datePosted: false,
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(
    getColumnPreference(MODULE_KEY, defaultColumnVisibility) ??
      defaultColumnVisibility,
  );

  const getData = async () => {
    try {
      let response;
      if (selectedCenter === 0 || selectedCenter === "0") {
        response = await api.get(`/getAllSmsPushNotifications`);
      } else {
        response = await api.get(
          `/getAllSmsPushNotifications?centerId=${selectedCenter}`,
        );
      }
      setDatas(response.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [selectedCenter]);

  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "id",
        header: "",
        size: 20,
        enableSorting: false,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      { accessorKey: "messageTitle", header: "Event Name" },
      { accessorKey: "messageDescription", header: "Message" },
      { accessorKey: "datePosted", header: "Created On" },
      // { accessorKey: "createdBy", header: "Created By" },
      // {
      //   accessorKey: "createdAt",
      //   header: "Created On",
      //   Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      // },
      // {
      //   accessorKey: "updatedAt",
      //   header: "Updated On",
      //   Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      // },
      // {
      //   accessorKey: "updatedBy",
      //   header: "Updated By",
      //   Cell: ({ cell }) => cell.getValue() || "",
      // },
    ],
    [],
  );

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454",
            backgroundColor: "#e6edf7",
            fontWeight: 400,
            fontSize: "13px",
            textAlign: "center",
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  const table = useMaterialReactTable({
    columns,
    data: datas,
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,

    state: {
      columnVisibility,
      isLoading: loading,
    },

    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },

    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },

    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigate(`/sendNotification/view/${row.original.id}`),
      style: { cursor: "pointer" },
    }),

    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, datas, "School_Announcement.csv")
        }
      >
        Export CSV
      </button>
    ),
  });

  return (
    <div className="container-fluid my-4">
      <ol
        className="breadcrumb my-3 px-2"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Messaging
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          School Announcement
        </li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="me-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                School Announcement
              </span>
            </span>
          </div>
        </div>

        <div className="mb-3 d-flex justify-content-end">
          <div className="me-2">
            {storedScreens?.sendNotificationCreate && (
              <Link to="/sendNotification/add">
                <button type="button" className="btn btn-button btn-sm">
                  Add <i className="bx bx-plus"></i>
                </button>
              </Link>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <ThemeProvider theme={theme}>
            <MaterialReactTable table={table} />
          </ThemeProvider>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => navigate(`/sendNotification/edit/${selectedId}`)}
            className="menuitem-style"
          >
            Edit
          </MenuItem>
          <MenuItem>
            <GlobalDelete
              path={`/deleteSmsPushNotifications/${selectedId}`}
              onDeleteSuccess={getData}
              onOpen={handleMenuClose}
            />
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default SendNotification;
